import React, { useState, useEffect, useRef, useCallback, useMemo, memo, lazy, Suspense } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
  UseFormWatch,
  UseFormSetError,
} from "react-hook-form";
import SelectInput from "../../../components/Select";
import FormErrorMessage from "../../../components/common/FormErrorMessage";
import {
  OrderFormData,
  AddressOption,
  LocationState,
} from "../../../types/types";
import {
  useDistrictFetchData,
} from "../../../apis/locationApis";
import { GULF_COUNTRIES, COUNTRY_FIELDS, FIELD_LABELS, parseNominatimResult } from "./GulfMapPicker";
import { useLanguage } from "../../../context/LanguageContext";

// Lazy-load the heavy map component so it doesn't block initial render
const GulfMapPicker = lazy(() => import("./GulfMapPicker"));

const API_BASE = "https://track-map.gwarm.net/api/areas";

interface SenderAddressProps {
  register: UseFormRegister<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  watch: UseFormWatch<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  clearErrors: UseFormClearErrors<OrderFormData>;
  control: Control<OrderFormData>;
  className?: string;
  cities: any;
  handleCitySearch?: (query: string) => void;
  isCitiesFetchPending?: boolean;
  selectedOption: AddressOption;
  setSelectedOption: React.Dispatch<React.SetStateAction<AddressOption>>;
  countries?: any[];
  onCountryChange?: (countryId: string) => void;
  setError: UseFormSetError<OrderFormData>;
  isSidebar?: boolean;
}

// ── Tiny spinner used in multiple places ──────────────────────────────────────
const Spinner = memo(() => (
  <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
  </svg>
));

// ── Map loading skeleton ──────────────────────────────────────────────────────
const MapSkeleton = () => {
  const { t } = useLanguage();
  return (
    <div style={{ height: 260, borderRadius: 12, border: "1px solid #e5e7eb", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ color: "#9ca3af", fontSize: 13 }}>{t("Loading map…")}</span>
    </div>
  );
};

// Compact helper row for sidebar
const Row = ({ label, required: req, children }: { label: string; required?: boolean; children: React.ReactNode }) => {
  const { t } = useLanguage();
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0">
      <span className="w-[38%] text-xs font-semibold text-slate-500 tracking-wide pt-2 shrink-0 leading-tight">
        {t(label)}{req && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

// Stacked helper row for sidebar grid
const StackedRow = ({ label, required: req, children }: { label: string; required?: boolean; children: React.ReactNode }) => {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-slate-500 tracking-wide leading-tight">
        {t(label)}{req && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      <div className="w-full">{children}</div>
    </div>
  );
};

const SenderAddress: React.FC<SenderAddressProps> = ({
  register,
  errors,
  setValue,
  watch,
  control,
  clearErrors,
  className,
  cities,
  handleCitySearch,
  isCitiesFetchPending,
  countries = [],
  onCountryChange,
  setError,
  isSidebar = false,
}) => {
  const { t } = useLanguage();
  const [showMap, setShowMap] = useState(false);

  // Pin state: what the user clicked/dragged on the map
  const [pinPosition, setPinPosition] = useState<{ lat: number; lng: number } | null>(null);
  // Snap state: coordinates returned by DB (zone match) → we move the pin here
  const [snappedPosition, setSnappedPosition] = useState<{ lat: number; lng: number } | null>(null);

  // Track whether this is the first pin in current session
  const isPinnedRef = useRef(false);

  const [shortAddressWarning, setShortAddressWarning] = useState<string>("");
  const [shortAddressLoading, setShortAddressLoading] = useState(false);
  const [shortAddressError, setShortAddressError] = useState<string>("");
  const [searchRef, setSearchRef] = useState<string>("");

  // Derive country code from form value
  const countryNameWatch = watch("sender.countryName");
  const senderLocationCity = watch("sender.city");

  const countryCode = useMemo(
    () => GULF_COUNTRIES.find(c => c.name.toLowerCase() === countryNameWatch?.toLowerCase())?.code || "OTHER",
    [countryNameWatch]
  );

  const latWatch = watch("sender.latitude");
  const lngWatch = watch("sender.longitude");

  useEffect(() => {
    const lat = parseFloat(latWatch || "");
    const lng = parseFloat(lngWatch || "");
    if (!isNaN(lat) && !isNaN(lng)) {
      if (!pinPosition || Math.abs(pinPosition.lat - lat) > 0.00001 || Math.abs(pinPosition.lng - lng) > 0.00001) {
        const newPos = { lat, lng };
        setPinPosition(newPos);
        setSnappedPosition(null);
      }
    } else {
      if (pinPosition !== null && (!latWatch && !lngWatch)) {
        setPinPosition(null);
        setSnappedPosition(null);
      }
    }
  }, [latWatch, lngWatch]);

  const {
    data: districts = [],
    isPending: isDistrictFetchPending,
    refetch: fetchSenderDistricts,
  } = useDistrictFetchData(senderLocationCity, searchRef, "map");

  const handleDistrictSearch = useCallback((query: string) => {
    if (query.trim().length >= 3) {
      setSearchRef(query);
      fetchSenderDistricts();
    }
  }, [fetchSenderDistricts]);

  // ── Apply address fields from API response ────────────────────────────────
  const applyAddressFromApi = useCallback((addr: any, activeCountryCode: string) => {
    setValue("sender.address", `${addr.buildingNumber || ""} ${addr.street || ""} ${addr.district || ""} ${addr.city || ""}`.trim());

    if (addr.city) {
      setValue("sender.cityName", addr.city);
      const cityData = cities?.find((c: any) => c.name.toLowerCase() === addr.city?.toLowerCase());
      setValue("sender.city", cityData?.id ?? null as any);
      if (cityData) fetchSenderDistricts();
    }

    if (addr.district) {
      setValue("sender.districtName", addr.district);
      const distData = districts?.find((d: any) => d.name.toLowerCase() === addr.district?.toLowerCase());
      setValue("sender.district", distData?.id ?? null as any);
      clearErrors("sender.district");
    }

    setValue("sender.streetName", addr.street || "");
    setValue("sender.buildingNumber", addr.buildingNumber || "");
    setValue("sender.zipCode", addr.postalCode || "");
  }, [cities, districts, setValue, fetchSenderDistricts]);

  // ── Map click / drag handler ──────────────────────────────────────────────
  const handleMapPick = useCallback(async (latLng: { lat: number; lng: number }, mapData: any) => {
    // ── INSTANT: pin drops exactly where the user clicked, no waiting ─────
    setPinPosition(latLng);
    setSnappedPosition(null);
    setValue("sender.latitude", latLng.lat.toString());
    setValue("sender.longitude", latLng.lng.toString());

    // Detect country from Nominatim & update dropdown if changed
    let activeCountryCode = countryCode;
    const detectedCode = mapData?.address?.country_code?.toUpperCase();
    if (detectedCode && detectedCode !== countryCode && GULF_COUNTRIES.some(c => c.code === detectedCode)) {
      activeCountryCode = detectedCode;
      const gulfName = GULF_COUNTRIES.find(c => c.code === detectedCode)?.name || "";
      const matched = countries.find((c: any) => c.name.toLowerCase() === gulfName.toLowerCase());
      if (matched && onCountryChange) onCountryChange(matched.id);
    }

    if (activeCountryCode === "SA") {
      setShortAddressLoading(true);
      setShortAddressError("");
      setShortAddressWarning("");
      setValue("sender.shortAddress", "");
      setValue("sender.nationalAddress", "");
      clearErrors("sender.shortAddress");
      clearErrors("sender.nationalAddress");

      // 1. Check if google map (Nominatim) data contains short address
      let foundShortAddress: string | null = null;
      if (mapData) {
        const textToSearch = JSON.stringify(mapData);
        const match = textToSearch.match(/\b([a-zA-Z]{4}\d{4})\b/);
        if (match) {
          foundShortAddress = match[1].toUpperCase();
        }
      }

      if (foundShortAddress) {
        setValue("sender.shortAddress", foundShortAddress);
        setValue("sender.nationalAddress", foundShortAddress);

        try {
          const checkRes = await fetch(`${API_BASE}/adrress-get-by-short-address?shortAddress=${foundShortAddress}`);
          const checkData = await checkRes.json();
          if (checkData?.success && checkData?.data) {
            const shortAddr = checkData.data;
            applyAddressFromApi(shortAddr, activeCountryCode);

            if (shortAddr.latitude && shortAddr.longitude) {
              const pos = { lat: parseFloat(shortAddr.latitude), lng: parseFloat(shortAddr.longitude) };
              setPinPosition(pos);
              setSnappedPosition(pos);
              setValue("sender.latitude", shortAddr.latitude.toString());
              setValue("sender.longitude", shortAddr.longitude.toString());
            }
            setShortAddressWarning("");
            setShortAddressError("");
          } else {
            setShortAddressError("Short Address verified lookup failed. Please type it manually.");
          }
        } catch (err) {
          console.error("Short address verification lookup failed:", err);
          setShortAddressError("Could not verify short address. Please try again.");
        } finally {
          setShortAddressLoading(false);
        }
        isPinnedRef.current = true;
        return;
      }

      // 2. Check coordinates API
      try {
        const res = await fetch(`${API_BASE}/find-by-coordinates?lat=${latLng.lat}&lon=${latLng.lng}`);
        const apiData = await res.json();

        if (apiData?.success && apiData?.data) {
          const addr = apiData.data;
          const source: string = addr.source ?? "database";
          applyAddressFromApi(addr, activeCountryCode);

          if (source === "redis_geo" || source === "database_zone") {
            if (addr.latitude && addr.longitude) {
              const snapped = { lat: parseFloat(addr.latitude), lng: parseFloat(addr.longitude) };
              setSnappedPosition(snapped);
              setPinPosition(snapped);
              setValue("sender.latitude", addr.latitude.toString());
              setValue("sender.longitude", addr.longitude.toString());
            }
          }

          const resolvedShortCode = addr.name;
          if (resolvedShortCode) {
            setValue("sender.shortAddress", resolvedShortCode);
            setValue("sender.nationalAddress", resolvedShortCode);

            try {
              const checkRes = await fetch(`${API_BASE}/adrress-get-by-short-address?shortAddress=${resolvedShortCode}`);
              const checkData = await checkRes.json();
              if (checkData?.success && checkData?.data) {
                const shortAddr = checkData.data;
                applyAddressFromApi(shortAddr, activeCountryCode);

                if (shortAddr.latitude && shortAddr.longitude) {
                  const pos = { lat: parseFloat(shortAddr.latitude), lng: parseFloat(shortAddr.longitude) };
                  setPinPosition(pos);
                  setSnappedPosition(pos);
                  setValue("sender.latitude", shortAddr.latitude.toString());
                  setValue("sender.longitude", shortAddr.longitude.toString());
                }

                if (source === "database_zone") {
                  setShortAddressWarning("📍 Approximate match. Pin again on the exact spot for precise short address.");
                } else {
                  setShortAddressWarning("");
                }
                setShortAddressError("");
              } else {
                setShortAddressError("Short Address verified lookup failed. Please type it manually.");
              }
            } catch (err) {
              console.error("Short address verification lookup failed:", err);
              setShortAddressError("Could not verify short address. Please try again.");
            }
          } else {
            setValue("sender.shortAddress", "");
            setValue("sender.nationalAddress", "");
            setShortAddressError("Short Address was not found from the map. Please fill it manually.");
          }

          isPinnedRef.current = true;
          setShortAddressLoading(false);
          return;
        }
      } catch (err) {
        console.error("find-by-coordinates failed:", err);
      }

      // 3. Fallback: geocoding but no short address found
      setShortAddressLoading(false);
      setValue("sender.shortAddress", "");
      setValue("sender.nationalAddress", "");
      setShortAddressWarning("");
      setShortAddressError("Short Address was not found from the map. Please fill it manually.");
    }

    const parsed: any = parseNominatimResult(mapData, activeCountryCode);
    setValue("sender.address", parsed.fullAddress || "");

    if (parsed.city) {
      setValue("sender.cityName", parsed.city);
      const cityData = cities?.find((c: any) => c.name.toLowerCase() === parsed.city.toLowerCase());
      setValue("sender.city", cityData?.id ?? null as any);
      if (cityData) fetchSenderDistricts();
    }

    const districtEquivalent = parsed.district || parsed.area || parsed.zone || parsed.block || parsed.wilayat;
    if (districtEquivalent) {
      setValue("sender.districtName", districtEquivalent);
      const distData = districts?.find((d: any) => d.name.toLowerCase() === districtEquivalent.toLowerCase());
      setValue("sender.district", distData?.id ?? null as any);
      clearErrors("sender.district");
    }

    setValue("sender.streetName", parsed.street || "");
    setValue("sender.buildingNumber", parsed.building || "");
    setValue("sender.zipCode", parsed.postalCode || parsed.poBox || "");

    if (activeCountryCode !== "SA" && parsed.shortAddress) {
      setValue("sender.shortAddress", parsed.shortAddress);
      setValue("sender.nationalAddress", parsed.shortAddress);
    }

    isPinnedRef.current = true;
  }, [countryCode, countries, onCountryChange, setValue, applyAddressFromApi, cities, districts, fetchSenderDistricts, clearErrors]);

  // ── Short address manual input ────────────────────────────────────────────
  const handleShortAddressChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase();
    setValue("sender.shortAddress", val);
    setValue("sender.nationalAddress", val);
    setShortAddressWarning("");
    setShortAddressError("");
    clearErrors("sender.shortAddress");
    clearErrors("sender.nationalAddress");

    if (val.length === 8) {
      setShortAddressLoading(true);
      try {
        const res = await fetch(`${API_BASE}/adrress-get-by-short-address?shortAddress=${val}`);
        const apiData = await res.json();
        if (apiData?.success && apiData?.data) {
          const addr = apiData.data;
          setShortAddressError("");
          applyAddressFromApi(addr, countryCode);

          if (addr.latitude && addr.longitude) {
            const pos = { lat: parseFloat(addr.latitude), lng: parseFloat(addr.longitude) };
            setPinPosition(pos);
            setSnappedPosition(pos);
            setValue("sender.latitude", addr.latitude.toString());
            setValue("sender.longitude", addr.longitude.toString());
          }
          clearErrors("sender.shortAddress");
          clearErrors("sender.nationalAddress");
        } else {
          setShortAddressError("Short code not found. Please check and enter a correct code.");
        }
      } catch (err) {
        console.error("Short address lookup failed:", err);
        setShortAddressError("Could not verify short address. Please try again.");
      } finally {
        setShortAddressLoading(false);
      }
    } else if (val.length === 0) {
      setPinPosition(null);
      setSnappedPosition(null);
    }
  }, [setValue, countryCode, applyAddressFromApi, clearErrors]);

  // Reset pin tracking when map is re-opened
  const handleToggleMap = useCallback(() => {
    setShowMap(prev => {
      if (!prev) isPinnedRef.current = false; // fresh open → reset
      return !prev;
    });
  }, []);

  const fieldKeys: string[] = useMemo(() => COUNTRY_FIELDS[countryCode] || COUNTRY_FIELDS["SA"], [countryCode]);

  const isPinned = !!pinPosition;
  const shortAddressVal = watch("sender.shortAddress");
  const shortAddressOk = !shortAddressLoading && !shortAddressError && shortAddressVal?.length === 8 && watch("sender.address");

  const compactInputCls = (hasErr?: boolean) =>
    `w-full text-sm rounded-lg border px-3 py-1.5 outline-none focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037]/30 transition-all ${hasErr ? "border-red-400 bg-red-50" : "border-gray-200 bg-white"}`;

  const gridFields = useMemo(() => {
    if (countryCode === "SA") {
      return ["shortAddress", "city", "district", "street", "latitude", "longitude", "building", "postalCode"];
    } else {
      const keys = fieldKeys.filter(k => k !== "shortAddress");
      const first4 = keys.slice(0, 4);
      const remaining = keys.slice(4);
      return [...first4, "latitude", "longitude", ...remaining];
    }
  }, [countryCode, fieldKeys]);

  const renderField = (key: string) => {
    if (key === "shortAddress") {
      return (
        <StackedRow key={key} label={t("Short Add")} required={countryCode === "SA"}>
          <div className="relative">
            <input
              {...(() => {
                const { onChange: formOnChange, ...rest } = register("sender.shortAddress", {
                  required: countryCode === "SA" ? "Short address is required" : false,
                  validate: (val) => {
                    if (countryCode === "SA" && val && val.length !== 8) {
                      return "Short address must be 8 characters";
                    }
                    return true;
                  }
                });
                return { ...rest, onChange: (e: any) => { formOnChange(e); handleShortAddressChange(e); } };
              })()}
              placeholder={t("e.g. RHOO2375")}
              autoComplete="off"
              disabled={shortAddressLoading}
              className={`w-full text-sm rounded-lg border px-3 py-1.5 outline-none pr-8 transition-all focus:border-[#FBD037] ${
                shortAddressLoading ? "border-blue-300 bg-blue-50" : (shortAddressError || errors.sender?.shortAddress) ? "border-red-400 bg-red-50" : shortAddressWarning ? "border-yellow-400 bg-white" : "border-gray-200 bg-white"
              }`}
            />
            <div className="absolute inset-y-0 right-2 flex items-center">
              {shortAddressLoading && <Spinner />}
              {!shortAddressLoading && (shortAddressError || errors.sender?.shortAddress) && <span className="text-red-500 text-sm">✕</span>}
              {shortAddressOk && <span className="text-green-500 text-sm">✓</span>}
            </div>
          </div>
          {shortAddressError && !shortAddressLoading && <p className="text-[10px] text-red-500 mt-0.5">⚠ {shortAddressError}</p>}
          {errors.sender?.shortAddress && !shortAddressError && !shortAddressLoading && (
            <p className="text-[10px] text-red-500 mt-0.5">⚠ {errors.sender?.shortAddress?.message}</p>
          )}
          {shortAddressWarning && !shortAddressError && !errors.sender?.shortAddress && !shortAddressLoading && <p className="text-[10px] text-yellow-600 mt-0.5">{shortAddressWarning}</p>}
        </StackedRow>
      );
    }

    const isCity = key === "city";
    const isDistrict = ["district", "area", "zone", "block", "wilayat", "governorate"].includes(key);
    let rhfName: any = `sender.${key}`;
    if (key === "street" || key === "road") rhfName = "sender.streetName";
    if (key === "building") rhfName = "sender.buildingNumber";
    if (key === "poBox" || key === "postalCode") rhfName = "sender.zipCode";
    const isRequiredField = false;
    const fieldKey = rhfName.replace(/^sender\./, "");
    const fieldError = (errors.sender as any)?.[fieldKey];

    if (isCity) return (
      <StackedRow key={key} label={t(FIELD_LABELS[key]?.label || "City / Town")} required>
        <SelectInput name="sender.city" label="" placeholder={t(FIELD_LABELS[key]?.placeholder)}
          selectOptions={cities?.map((c: any) => ({ id: c.id, value: c.id, text: c.name })) || []}
          value={watch("sender.city") || watch("sender.cityName")}
          setValue={(val) => {
            const m = cities?.find((c: any) => c.id === val);
            if (m) {
              setValue("sender.city", m.id);
              setValue("sender.cityName", m.name);
            } else {
              setValue("sender.city", null as any);
              setValue("sender.cityName", val);
            }
          }}
          onSearch={handleCitySearch} isPending={isCitiesFetchPending} register={register}
          validation={{ required: "City is required" }} error={errors.sender?.city} clearErrors={clearErrors} allowCustom={true} />
      </StackedRow>
    );

    if (isDistrict) return (
      <StackedRow key={key} label={t(FIELD_LABELS[key]?.label || "District")} required>
        <SelectInput name="sender.district" label="" placeholder={t(FIELD_LABELS[key]?.placeholder)}
          selectOptions={districts?.map((d: any) => ({ id: d.id, value: d.id, text: d.name })) || []}
          value={watch("sender.district") || watch("sender.districtName")}
          setValue={(val) => {
            const m = districts?.find((d: any) => d.id === val);
            if (m) {
              setValue("sender.district", m.id);
              setValue("sender.districtName", m.name);
            } else {
              setValue("sender.district", null as any);
              setValue("sender.districtName", val);
            }
          }}
          onSearch={handleDistrictSearch} isPending={isDistrictFetchPending} register={register} clearErrors={clearErrors}
          validation={{ validate: (val) => { const dn = watch("sender.districtName"); return (!val && !dn) ? `${FIELD_LABELS[key]?.label || "District"} is required` : true; } }}
          error={errors.sender?.district} allowCustom={true} />
      </StackedRow>
    );

    const fieldValidation = undefined;

    return (
      <StackedRow key={key} label={t(FIELD_LABELS[key]?.label || key)} required={isRequiredField}>
        <input {...register(rhfName, fieldValidation)} placeholder={t(FIELD_LABELS[key]?.placeholder)}
          className={compactInputCls(!!fieldError)} />
        {fieldError && <FormErrorMessage error={fieldError} />}
      </StackedRow>
    );
  };

  if (isSidebar) {
    return (
      <div className="w-full mt-1 space-y-3">
        {/* Map toggle compact */}
        <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
            {t("Map")} {isPinned && <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">📍 {t("Pinned")}</span>}
          </span>
          <button type="button" onClick={handleToggleMap}
            className="px-2.5 py-1 rounded-md bg-[#4F6EF7] text-white text-xs font-semibold hover:bg-[#3d59c9] transition-colors">
            {showMap ? t("Hide Map") : t("Open Map")}
          </button>
        </div>

        {showMap && (
          <div className="py-2 border-b border-slate-100">
            <Suspense fallback={<MapSkeleton />}>
              <GulfMapPicker country={countryCode} onPick={handleMapPick} pinPosition={pinPosition} snappedPosition={snappedPosition} />
            </Suspense>
          </div>
        )}

        {/* Grid for small fields */}
        <div className="grid grid-cols-2 gap-3 relative z-10">
          {gridFields.map((key) => {
            if (key === "latitude") {
              return (
                <StackedRow key="latitude" label={t("Latitude")} required={false}>
                  <input {...register("sender.latitude")}
                    placeholder={t("e.g. 24.7136")} className={compactInputCls(!!errors.sender?.latitude)} />
                  <FormErrorMessage error={errors.sender?.latitude} />
                </StackedRow>
              );
            }
            if (key === "longitude") {
              return (
                <StackedRow key="longitude" label={t("Longitude")} required={false}>
                  <input {...register("sender.longitude")}
                    placeholder={t("e.g. 46.6753")} className={compactInputCls(!!errors.sender?.longitude)} />
                  <FormErrorMessage error={errors.sender?.longitude} />
                </StackedRow>
              );
            }
            return renderField(key);
          })}
        </div>

        {/* Full Address - large field, full width */}
        <StackedRow label={t("Full Address")} required>
          <input
            {...register("sender.address", { required: "Full address is required" })}
            placeholder={t("Enter full address")}
            className={compactInputCls(!!errors.sender?.address)}
          />
          <FormErrorMessage error={errors.sender?.address} />
        </StackedRow>
      </div>
    );
  }

  // ── Non-sidebar: original card layout ──
  return (
    <div className={`bg-[#fdfdf6] rounded-xl space-y-6 mt-2 p-4 border w-full ${className}`}>
      <div className="space-y-4">

        {/* Map Toggle */}
        <div className="flex justify-between items-center w-full">
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            {t("Pin Location on Map")}
            {isPinned && <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">📍 {t("Pinned")}</span>}
          </label>
          <button
            type="button"
            onClick={handleToggleMap}
            className="px-3 py-1.5 rounded-md bg-[#4F6EF7] text-white text-xs font-semibold hover:bg-[#3d59c9] transition-colors"
          >
            {showMap ? t("Hide Map") : t("Open Map")}
          </button>
        </div>

        {/* The Map — lazy loaded */}
        {showMap && (
          <Suspense fallback={<MapSkeleton />}>
            <GulfMapPicker
              country={countryCode}
              onPick={handleMapPick}
              pinPosition={pinPosition}
              snappedPosition={snappedPosition}
            />
          </Suspense>
        )}

        {/* Short Address & Full Address */}
        {countryCode === "SA" ? (
          <div className={`flex gap-4 w-full ${isSidebar ? "flex-col" : "flex-row"}`}>
            <div className={`relative ${isSidebar ? "w-full" : "w-[30%]"}`}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Short Add / National Address")} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  {...(() => {
                    const { onChange: formOnChange, ...rest } = register("sender.shortAddress", {
                      required: countryCode === "SA" ? "Short address is required" : false,
                      validate: (val) => {
                        if (countryCode === "SA" && val && val.length !== 8) {
                          return "Short address must be 8 characters";
                        }
                        return true;
                      }
                    });
                    return {
                      ...rest,
                      onChange: (e: any) => {
                        formOnChange(e);
                        handleShortAddressChange(e);
                      }
                    };
                  })()}
                  placeholder={t("e.g. RHOO2375")}
                  autoComplete="off"
                  disabled={shortAddressLoading}
                  className={`w-full rounded-lg py-2 px-3 border outline-none pr-9 transition-all
                    ${
                      shortAddressLoading ? "border-blue-300 bg-blue-50" :
                      shortAddressError ? "border-red-400 bg-red-50" :
                      shortAddressWarning ? "border-yellow-500 bg-white" :
                      "border-gray-300 bg-white"
                    } focus:border-[#FBD037]`}
                />
                <div className="absolute inset-y-0 right-2 flex items-center">
                  {shortAddressLoading && <Spinner />}
                  {!shortAddressLoading && shortAddressError && <span className="text-red-500 text-base">✕</span>}
                  {shortAddressOk && <span className="text-green-500 text-base">✓</span>}
                </div>
              </div>
              {shortAddressLoading && (
                <div className="mt-1 flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay: '0ms'}}/>
                  <div className="h-2 w-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay: '150ms'}}/>
                  <div className="h-2 w-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay: '300ms'}}/>
                  <span className="text-xs text-blue-500 font-medium">{t("Looking up address…")}</span>
                </div>
              )}
              {shortAddressError && !shortAddressLoading && (
                <FormErrorMessage error={{ message: `⚠ ${shortAddressError}` }} />
              )}
              {errors.sender?.shortAddress && !shortAddressError && !shortAddressLoading && (
                <FormErrorMessage error={errors.sender?.shortAddress} />
              )}
              {shortAddressWarning && !shortAddressError && !shortAddressLoading && (
                <span className="text-xs text-yellow-600 mt-1 block font-medium">{shortAddressWarning}</span>
              )}
            </div>
            <div className={isSidebar ? "w-full" : "w-[70%]"}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("Full Address Line")} <span className="text-red-500">*</span></label>
              <input
                {...register("sender.address", { required: "Full address is required" })}
                placeholder={t("Enter full address details")}
                className={`w-full rounded-lg py-2 px-3 border outline-none ${
                  errors.sender?.address ? "border-red-500" : "border-gray-300"
                } focus:border-[#FBD037]`}
              />
              <FormErrorMessage error={errors.sender?.address} />
            </div>
          </div>
        ) : (
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("Full Address Line")} <span className="text-red-500">*</span></label>
            <input
              {...register("sender.address", { required: "Full address is required" })}
              placeholder={t("Enter full address details")}
              className={`w-full rounded-lg py-2 px-3 border outline-none ${
                errors.sender?.address ? "border-red-500" : "border-gray-300"
              } focus:border-[#FBD037]`}
            />
            <FormErrorMessage error={errors.sender?.address} />
          </div>
        )}

        {/* Latitude & Longitude */}
        <div className={`grid gap-4 ${isSidebar ? "grid-cols-1" : "grid-cols-2"}`}>
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("Latitude")}</label>
            <input
              {...register("sender.latitude")}
              placeholder={t("e.g. 24.7136")}
              className={`w-full rounded-lg py-2 px-3 border outline-none ${
                errors.sender?.latitude ? "border-red-500" : "border-gray-300"
              } focus:border-[#FBD037]`}
            />
            <FormErrorMessage error={errors.sender?.latitude} />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("Longitude")}</label>
            <input
              {...register("sender.longitude")}
              placeholder={t("e.g. 46.6753")}
              className={`w-full rounded-lg py-2 px-3 border outline-none ${
                errors.sender?.longitude ? "border-red-500" : "border-gray-300"
              } focus:border-[#FBD037]`}
            />
            <FormErrorMessage error={errors.sender?.longitude} />
          </div>
        </div>

        {/* Dynamic Fields */}
        <div className={`grid gap-4 ${isSidebar ? "grid-cols-1" : "grid-cols-2"}`}>
          {fieldKeys.filter(k => k !== "shortAddress").map((key) => {
            const isCity = key === "city";
            const isDistrict = ["district", "area", "zone", "block", "wilayat"].includes(key);

            if (isCity) {
              return (
                <div key={key} className="col-span-1">
                  <SelectInput
                    name="sender.city"
                    label={t(FIELD_LABELS[key]?.label || "City / Town")}
                    placeholder={t(FIELD_LABELS[key]?.placeholder)}
                    selectOptions={cities?.map((c: any) => ({ id: c.id, value: c.id, text: c.name })) || []}
                    value={watch("sender.city") || watch("sender.cityName")}
                    setValue={(val) => {
                      const m = cities?.find((c: any) => c.id === val);
                      if (m) {
                        setValue("sender.city", m.id);
                        setValue("sender.cityName", m.name);
                      } else {
                        setValue("sender.city", null as any);
                        setValue("sender.cityName", val);
                      }
                    }}
                    onSearch={handleCitySearch}
                    isPending={isCitiesFetchPending}
                    register={register}
                    validation={{ required: "City is required" }}
                    error={errors.sender?.city}
                    clearErrors={clearErrors}
                    allowCustom={true}
                  />
                </div>
              );
            }

            if (isDistrict) {
              return (
                <div key={key} className="col-span-1">
                  <SelectInput
                    name="sender.district"
                    label={t(FIELD_LABELS[key]?.label || "District")}
                    placeholder={t(FIELD_LABELS[key]?.placeholder)}
                    selectOptions={districts?.map((d: any) => ({ id: d.id, value: d.id, text: d.name })) || []}
                    value={watch("sender.district") || watch("sender.districtName")}
                    setValue={(val) => {
                      const m = districts?.find((d: any) => d.id === val);
                      if (m) {
                        setValue("sender.district", m.id);
                        setValue("sender.districtName", m.name);
                      } else {
                        setValue("sender.district", null as any);
                        setValue("sender.districtName", val);
                      }
                    }}
                    onSearch={handleDistrictSearch}
                    isPending={isDistrictFetchPending}
                    register={register}
                    clearErrors={clearErrors}
                    validation={{
                      validate: (val) => {
                        const dn = watch("sender.districtName");
                        return (!val && !dn) ? `${FIELD_LABELS[key]?.label || "District"} is required` : true;
                      }
                    }}
                    error={errors.sender?.district}
                    allowCustom={true}
                  />
                </div>
              );
            }

            // Other dynamic fields
            let rhfName: any = `sender.${key}`;
            if (key === "street" || key === "road") rhfName = "sender.streetName";
            if (key === "building") rhfName = "sender.buildingNumber";
            if (key === "poBox" || key === "postalCode") rhfName = "sender.zipCode";

            const isRequiredField = false;
            const fieldKey = rhfName.replace(/^sender\./, "");
            const fieldError = (errors.sender as any)?.[fieldKey];

            return (
              <div key={key} className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t(FIELD_LABELS[key]?.label || key)} {isRequiredField && <span className="text-red-500">*</span>}
                </label>
                <input
                  {...register(rhfName, isRequiredField ? { required: `${FIELD_LABELS[key]?.label || "This field"} is required` } : undefined)}
                  placeholder={t(FIELD_LABELS[key]?.placeholder)}
                  className={`w-full rounded-lg py-2 px-3 border outline-none ${
                    fieldError ? "border-red-500" : "border-gray-300"
                  } focus:border-[#FBD037]`}
                />
                {fieldError && <FormErrorMessage error={fieldError} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(SenderAddress);
